import { Box, Section, Text } from "@vinomofo/components";
import { useEffect, useMemo, useState } from "react";

import { COLORS } from "../../styles";
import { CircleMinusIcon } from "@vinomofo/icons";
import { NavigationButton } from "./NavigationButton";
import { StateResultsProvided } from "react-instantsearch-core";
import { connectStateResults } from "react-instantsearch-dom";
import qs from "qs";
import styled from "styled-components";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useLocation } from "@reach/router";
import algoliasearch from "algoliasearch";
import useAuth from "../../hooks/use-auth";
import { determineTierIndex } from "../../utils/loyalty";
import { VinoUser } from "../../contexts/auth/state";
import { useLoyalty } from "../../hooks/account/use-loyalty";
import { hasSpiritsInPath } from "../../utils/is-page";

const WHISKYMOFO_ENABLED = process.env.GATSBY_ENABLE_WHISKYMOFO === "true";

const StyledSection = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  & * {
    margin-bottom: -4px;
  }
`;
const CloseButton = styled.div`
  color: ${COLORS.dorianGray};
  cursor: pointer;
  float: right;
  &:hover {
    color: ${COLORS.mofoGreen};
  }
`;
interface CategoryNavigationItem {
  title: string;
  primaryColor: string;
  linkTo: {
    slug: string;
    path: string;
  };
  referencedCategory: string;
}

interface CategoryNavigationProps extends StateResultsProvided {
  navigationItems: Array<CategoryNavigationItem>;
}
type FilterQueryParams = {
  query?: string;
};

const urlToSearchState = (location) => {
  const { query = "", ...params } = qs.parse(
    location.search.slice(1)
  ) as FilterQueryParams;
  return {
    query: decodeURIComponent(query),
  };
};

const NoResultQuery = (): JSX.Element => {
  const { searching, hasResult } = useInstantSearch();
  const location = useLocation();
  const { query } = urlToSearchState(location);
  const [shouldBeDisplayed, setShouldBeDisplayed] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setShouldBeDisplayed(true);
    }, 600);
  }, [query]);

  return query.length > 0 && shouldBeDisplayed && !hasResult && !searching ? (
    <Box
      textAlign="right"
      display="grid"
      padding={["20px", "50px"]}
      width="100%"
      backgroundColor="almostWhite"
      mt={[30, 40, 50]}
    >
      <StyledSection>
        <Text fontSize={["14px", "16px", "18px"]} textAlign="justify">
          <strong>{query}?</strong>
          {` You have exquisite taste.
            But maybe you typed in haste? Why not check these out instead?`}
        </Text>
        <CloseButton
          className="searchErrorCloseButton"
          onClick={() => {
            setShouldBeDisplayed(false);
          }}
        >
          <CircleMinusIcon color="inherit" />
        </CloseButton>
      </StyledSection>
    </Box>
  ) : null;
};

export const CategoryNavigation = connectStateResults<CategoryNavigationProps>(
  (props: CategoryNavigationProps): JSX.Element => {
    const LOCATION = useLocation();
    const { pathname, search } = LOCATION;
    const [result, setResult] = useState<number[] | null>(null);
    const { user } = useAuth();
    const { loyalty, showLoyalty } = useLoyalty();
    const isSpiritPage = hasSpiritsInPath(pathname);
    const { index } = determineTierIndex(
      user as VinoUser,
      showLoyalty,
      loyalty?.loyalty_tier
    );

    const doSearch = async (search) => {
      const appID = process.env.GATSBY_ALGOLIA_APP_ID;
      const appKEY = process.env.GATSBY_ALGOLIA_SEARCH_KEY;
      const client = algoliasearch(appID, appKEY);
      const { results } = await client.multipleQueries(search);
      if (!results) setResult(null);
      if (results) setResult(results.map((i) => i.nbHits));
    };

    const getCategoryFilters = () => {
      if (isSpiritPage) {
        return [
          { filter: "category:Whisky AND region:Scotland", key: "Scotch" },
          { filter: "category:Whisky AND region:USA", key: "Bourbon" },
          { filter: "category:Gin", key: "Gin" },
          { filter: "category:Rum", key: "Rum" },
          {
            filter:
              "isSpirit:True AND NOT category:Whisky AND NOT category:Gin AND NOT category:Rum",
            key: "Other Spirits",
          },
        ];
      }
      return [
        { filter: "category:Red", key: "Red" },
        { filter: "category:White", key: "White" },
        {
          filter: "category:Rose OR category:Sparkling",
          key: "Rosé & Sparkling",
        },
        { filter: "isMixed:True", key: "Mixed" },
        { filter: "secret:True", key: "Black Market" },
      ];
    };

    useEffect(() => {
      if (!result) {
        const filters = getCategoryFilters();
        doSearch(
          filters.map(({ filter }) => ({
            indexName: index,
            params: {
              filters: filter,
              hitsPerPage: 0,
            },
          }))
        );
      }
    }, [result, isSpiritPage, index]);

    const { carryOverFilter } = useInstantSearch();
    const savedFilter = (carryOverFilter || search).replace(
      /(\?|&)(query|page)=[^&]+/g,
      ""
    );

    return props.navigationItems.length > 0 ? (
      <>
        <Section mb={["-10px", "-20px", "-30px"]}>
          <Box
            display="flex"
            width="100%"
            justifyContent="flex-start"
            overflow="auto"
            pb="10px"
          >
            {props.navigationItems.map((item, i) => {
              const { title, primaryColor, linkTo } = item;
              const href = linkTo.path;
              const count = result?.[i] ?? null;

              return useMemo(
                () => (
                  <NavigationButton
                    active={pathname === href}
                    key={i}
                    label={title}
                    borderColor={primaryColor}
                    href={
                      pathname === href
                        ? `/${
                            isSpiritPage && WHISKYMOFO_ENABLED
                              ? "spirits"
                              : "wines"
                          }${savedFilter}`
                        : href.endsWith("/")
                        ? `${href}${savedFilter}`
                        : `${href}/${savedFilter}`
                    }
                    count={count}
                  />
                ),
                [props.navigationItems, count, LOCATION, carryOverFilter]
              );
            })}
          </Box>
        </Section>
        <NoResultQuery />
      </>
    ) : null;
  }
);
